import { Injectable, inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class LinkService {
    private readonly document = inject(DOCUMENT);
    private readonly renderer: Renderer2;

    constructor(rendererFactory: RendererFactory2) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    /**
     * Updates or creates a link tag in the document head
     * @param link Object with link attributes (rel, href, hreflang, etc.)
     */
    updateTag(link: any): void {
        if (!link.rel || !link.href) {
            return;
        }

        // Check if a similar link tag already exists
        const selector = link.hreflang
            ? `link[rel="${link.rel}"][hreflang="${link.hreflang}"]`
            : `link[rel="${link.rel}"][href="${link.href}"]`;

        let existingLink = this.document.head.querySelector(selector);

        if (existingLink) {
            // Update existing link
            Object.keys(link).forEach(attr => {
                if (attr !== 'type' && link[attr]) {
                    this.renderer.setAttribute(existingLink, attr, link[attr]);
                }
            });
        } else {
            // Create new link element
            const linkElement = this.renderer.createElement('link');
            Object.keys(link).forEach(attr => {
                if (attr !== 'type' && link[attr]) {
                    this.renderer.setAttribute(linkElement, attr, link[attr]);
                }
            });
            this.renderer.appendChild(this.document.head, linkElement);
        }
    }

    /**
     * Removes all link tags with the specified rel attribute
     * @param rel The rel attribute value to filter by (e.g., 'canonical', 'alternate')
     */
    removeByRel(rel: string): void {
        const links = this.document.head.querySelectorAll(`link[rel="${rel}"]`);
        links.forEach(link => {
            this.renderer.removeChild(this.document.head, link);
        });
    }

    /**
     * Removes a specific link tag
     * @param selector CSS selector to identify the link tag
     */
    removeTag(selector: string): void {
        const link = this.document.head.querySelector(selector);
        if (link) {
            this.renderer.removeChild(this.document.head, link);
        }
    }
}
